import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Clock, BadgeCheck, ArrowRight, ArrowUpRight, GraduationCap } from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton, OutlineButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";
import { CPD_COURSES } from "@/lib/cpd";

const B = ({ children }) => (
  <li className="flex items-start gap-3 text-white/70 leading-relaxed">
    <CheckCircle2 size={16} strokeWidth={1.4} className="text-gold shrink-0 mt-1" /><span>{children}</span>
  </li>
);

export function CPDCoursesList() {
  return (
    <>
      <section className="pt-40 md:pt-52 pb-14 md:pb-18">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Overline className="mb-5 block">CPD Training Courses</Overline>
          <MaskedLines
            className="font-playfair uppercase leading-[0.95] tracking-tight"
            lineClassName="text-5xl md:text-7xl"
            lines={[<span key="1" className="text-white">Master.</span>, <span key="2" className="text-gold">Perform.</span>]}
          />
          <p className="mt-8 max-w-2xl text-white/65 text-lg leading-relaxed">
            {CPD_COURSES.length} CPD training courses, each combining comprehensive theory with hands-on practical training in small class sizes.
          </p>
        </div>
      </section>
      <section className="pb-28 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {CPD_COURSES.map((c, i) => (
            <Reveal key={c.id} delay={(i % 3) * 0.08}>
              <Link
                to={`/courses/cpd/${c.id}`}
                data-testid={`cpd-card-${c.id}`}
                className="group block h-full border border-white/10 bg-navy-surface hover:border-gold/40 transition-colors p-8"
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-gold"><GraduationCap size={14} strokeWidth={1.4} /> CPD</span>
                  <span className="font-playfair text-2xl text-gold">{c.price}</span>
                </div>
                <h3 className="font-playfair text-xl md:text-2xl text-white leading-snug group-hover:text-gold transition-colors">{c.name}</h3>
                <div className="hairline w-16 my-5" />
                <p className="text-white/55 text-sm leading-relaxed line-clamp-4">{c.overview}</p>
                <span className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.2em] uppercase text-white group-hover:text-gold transition-colors">
                  View course <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
}

export default function CPDCourse() {
  const { slug } = useParams();
  const { openEnquiry } = useEnquiry();
  const c = CPD_COURSES.find(x => x.id === slug);
  if (!c) return (
    <section className="pt-40 pb-40 text-center max-w-2xl mx-auto px-6">
      <h2 className="font-playfair text-4xl text-white mb-6">Course not found</h2>
      <Link to="/courses/cpd" className="text-gold underline">Back to all CPD courses</Link>
    </section>
  );
  return (
    <>
      <section className="pt-40 md:pt-48 pb-10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/courses/cpd" className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-white/60 hover:text-gold transition-colors mb-8" data-testid="cpd-back-link">
            <ArrowUpRight size={14} className="-rotate-180" /> All CPD courses
          </Link>
          <Overline className="mb-4 block">CPD Training</Overline>
          <h1 className="font-playfair text-4xl md:text-6xl text-white leading-tight max-w-4xl">{c.name}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-6 text-[12px] tracking-[0.2em] uppercase text-gold">
            <span className="inline-flex items-center gap-2"><BadgeCheck size={16} strokeWidth={1.4} /> Certificated CPD</span>
            <span className="inline-flex items-center gap-2"><Clock size={16} strokeWidth={1.4} /> 1 Day</span>
            <span className="font-playfair text-3xl md:text-4xl text-gold not-italic tracking-normal">{c.price}</span>
          </div>
        </div>
      </section>
      <section className="pb-24 bg-navy">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
          <div className="lg:col-span-2 space-y-12">
            <Reveal>
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold mb-4">Overview</h3>
              <p className="text-white/75 text-base md:text-lg leading-relaxed">{c.overview}</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold mb-5">Learning Outcomes</h3>
              <ul className="space-y-2.5">{c.learn.map((l,i)=><B key={i}>{l}</B>)}</ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold mb-5">Who Is This Course Suitable For?</h3>
              <ul className="space-y-2.5">{c.suitable.map((l,i)=><B key={i}>{l}</B>)}</ul>
            </Reveal>
            <Reveal delay={0.15}>
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold mb-4">Entry Requirements</h3>
              <p className="text-white/75 leading-relaxed">{c.entry}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <h3 className="text-xs tracking-[0.2em] uppercase text-gold mb-5">Course Inclusions</h3>
              <ul className="space-y-2.5">{c.includes.map((l,i)=><B key={i}>{l}</B>)}</ul>
            </Reveal>
          </div>
          <aside className="lg:col-span-1">
            <div className="border border-gold/25 bg-navy-surface p-8 sticky top-28">
              <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-3">Course Investment</p>
              <p className="font-playfair text-5xl text-white mb-6">{c.price}</p>
              <div className="hairline mb-6" />
              <GoldButton className="w-full" onClick={() => openEnquiry(c.name)} data-testid={`cpd-enquire-${c.id}`}>
                Enquire About This Course <ArrowRight size={16} strokeWidth={2} />
              </GoldButton>
              <OutlineButton className="w-full mt-3" onClick={() => openEnquiry(c.name)} data-testid={`cpd-more-${c.id}`}>
                Request Full Details
              </OutlineButton>
              <p className="text-white/40 text-xs mt-6 leading-relaxed">Flexible payment options available. Ask us about Klarna, Clearpay and Pay It Later on your enquiry.</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
